<template>
  <div
    v-if="sampleLoaded"
    class="section-sample h-100"
  >
    <div class="h-100 d-flex flex-column justify-content-between">
      <div class="d-flex flex-column flex-grow-1 section-sample-layout-height">
        <b-row class="d-flex align-items-center mb-2 mt-1 h-auto">
          <b-col>
            <span
              qa="diagnosis-section-sample"
              class="ap-title ap-title__main"
            >{{ $t('diagnosis.sections.sampleInfo') }}</span>
          </b-col>
          <b-col
            cols="auto"
            class="d-flex justify-content-end"
          >
            <div>
              <b-button
                id="pop-label"
                v-tooltip="$t('global.tags')"
                class="btn__icon btn__icon--primary  px-2"
                style="position:relative"
              >
                <i
                  class="fas fa-send-backward"
                  aria-hidden="true"
                />
                <vt-bubble
                  class="tag-indicator"
                  :number="sampleInformationTags.length"
                  :info="true"
                />
              </b-button>
              <span>
                <b-button
                  id="sample-more"
                  class="btn__icon btn__icon--primary pr-2 pl-3"
                  :disabled="disabledPopoverButton"
                >
                  <i
                    class="fas fa-ellipsis-v"
                    aria-hidden="true"
                  />
                </b-button>
                <b-popover
                  target="sample-more"
                  placement="bottom"
                  triggers="click blur"
                >
                  <div class="ap-popover">
                    <ul>
                      <li
                        v-if="$_canCreateIncident()"
                      >
                        <b-button
                          :disabled="!$_canBeIncidentCreatedToSample(sampleInformation)"
                          @click="$emit('openIncidentFormModal', { coreEntity: 'SAMPLE', entitiesIds: { casId, samId } })"
                        >
                          {{ $t('diagnosis.btns.incident') }}
                        </b-button>
                      </li>
                      <li
                        v-if="$_canAttachDocumentToSample()"
                      >
                        <b-button
                          :disabled="!$_canBeDocumentAttachedToSample(sampleInformation)"
                          @click="$emit('openAddFileModal', [labId, casId, samId])"
                        >
                          {{ $t('diagnosis.btns.attach') }}
                        </b-button>
                      </li>
                      <li>
                        <b-button
                          @click="showInGlobalLocator(sampleLabel)"
                        >
                          {{ $t('global.showInGlobalLocator') }}
                        </b-button>
                      </li>
                    </ul>
                  </div>
                </b-popover>
              </span>
            </div>
          </b-col>
        </b-row>
        <div class="sample-scroll-section flex-grow-1">
          <b-container
            fluid
            class="px-1"
          >
            <b-row v-if="sample.currentLaboratory.labId !== labId">
              <b-col>
                <alert-banner
                  :variant="'info'"
                  :text="`${$t('global.elementLocatedIn')} ${sample.currentLaboratory.labName}`"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col class="mt-1">
                <div class="form-card">
                  <b-row class="mb-2">
                    <b-col
                      md="12"
                      lg
                      class="d-flex align-items-center"
                    >
                      <div class="d-flex align-items-center">
                        <span
                          class="ap-title ap-title__section"
                          qa="diagnosis-sample-label"
                        >
                          <i
                            class="far fa-list-ul mr-2"
                            aria-hidden="true"
                          />
                          {{ sampleLabel }}
                        </span>
                        <div
                          v-if="sample.isDepleted"
                          class="ml-3"
                        >
                          <span class="depleted-text"><i
                            class="far fa-empty-set mr-1"
                            aria-hidden="true"
                          />{{ $t('global.depletedSample') }}</span>
                        </div>
                      </div>
                      <div class="d-flex ml-2">
                        <b-popover
                          target="pop-label"
                          title="Popover"
                          placement="bottom"
                          triggers="click blur"
                        >
                          <div class="pop-labels ap-popover">
                            <div class="pop-labels-container">
                              <b-form-checkbox-group v-model="sampleInformationTags">
                                <div
                                  v-for="tag in sampleTags"
                                  :key="tag.id"
                                  class="pop-labels-row d-flex justify-content-between py-2 px-1"
                                >
                                  <div class="pl-2">
                                    <i
                                      class="fas fa-square ml-1 mr-2"
                                      :style="'color: '+tag.color"
                                      aria-hidden="true"
                                    />
                                    <span>{{ tag.name }}</span>
                                  </div>
                                  <b-form-checkbox
                                    :class="{'disabled':disabledTagsEdition}"
                                    :disabled="disabledTagsEdition"
                                    :value="tag"
                                  />
                                </div>
                              </b-form-checkbox-group>
                            </div>
                          </div>
                        </b-popover>
                      </div>
                    </b-col>
                    <b-col
                      cols="auto"
                    >
                      <span
                        v-if="hasURVActive"
                        class="alt-color"
                      >{{ $t('diagnosis.labels.valueUnit') }}: {{ totalURV }}</span>
                      <span
                        v-if="hasUCLActive"
                        class="px-3"
                      >|</span>
                      <span
                        v-if="hasUCLActive"
                        class="alt-color"
                      >{{ $t('diagnosis.labels.workUnit') }}: {{ totalUCL }}</span>
                    </b-col>
                  </b-row>
                  <b-row class="col-grid">
                    <b-col
                      class="field"
                      xl="3"
                      lg="6"
                      md="12"
                    >
                      <div class="form-label">
                        <label>{{ $t('diagnosis.grid.sampleGrid.location') }}</label>
                      </div>
                      <vt-combo-select
                        v-model="sampleInformation.topographicCode"
                        v-validate="'required'"
                        qa="section-sample-location-div"
                        :disabled="$_disabledTopographicCode(sampleInformation)"
                        label="description"
                        :allow-empty="false"
                        data-vv-name="topographicCode"
                        data-vv-scope="samples"
                        :option-loader="loadTopographicCodes"
                      />
                    </b-col>
                    <b-col
                      class="field"
                      xl="3"
                      lg="6"
                      md="12"
                    >
                      <div class="form-label">
                        <label>{{ $t('diagnosis.grid.sampleGrid.sampleType') }}</label>
                      </div>
                      <vt-combo-select
                        id="sampleType"
                        v-model="sampleInformation.sampleType"
                        v-validate="'required'"
                        qa="section-sample-sample-type-div"
                        :disabled="$_disabledSampleType(sampleInformation)"
                        :allow-empty="false"
                        track-by="id"
                        label="description"
                        data-vv-name="sampleType"
                        data-vv-scope="samples"
                        :option-loader="loadSampleTypes"
                        @input="changeSelectedSampleType"
                      />
                    </b-col>
                    <b-col
                      class="field"
                      xl="6"
                      lg="6"
                      md="12"
                    >
                      <div class="form-label">
                        <label>{{ $t('diagnosis.grid.sampleGrid.procedure') }}</label>
                      </div>
                      <vt-combo-select
                        v-model="sampleInformation.procedure"
                        qa="section-sample-procedure-div"
                        :disabled="$_disabledProcedure(sampleInformation)"
                        label="description"
                        :option-loader="loadProcedures"
                      />
                    </b-col>
                    <b-col
                      class="field"
                      xl="6"
                      lg="6"
                      md="12"
                    >
                      <div class="form-label">
                        <label>{{ $t('diagnosis.grid.sampleGrid.mostLikelyDiagnosis') }}</label>
                      </div>
                      <vt-combo-select
                        v-model="sampleInformation.mostLikelyMorphologicCode"
                        qa="section-sample-most-likely-div"
                        :disabled="$_disabledMostLikelyMorphologicCode(sampleInformation)"
                        label="description"
                        :option-loader="loadMostLikelyMorphologicCodes"
                      />
                    </b-col>
                    <b-col
                      xl="6"
                      lg="12"
                    >
                      <b-row>
                        <b-col
                          lg="auto"
                          md="12"
                        >
                          <div class="form-label">
                            <label>{{ $t('grossing.content.partial') }}</label>
                          </div>
                          <div class="d-flex">
                            <div class="col-3 switch-btn p-0">
                              <input
                                id="toggle"
                                v-model="partialEmbedding"
                                disabled
                                class="offscreen"
                                type="checkbox"
                                aria-hidden="true"
                              >
                              <label
                                qa="section-sample-partial-embedding-label"
                                for="toggle"
                                class="switch"
                              />
                            </div>
                          </div>
                        </b-col>
                        <b-col
                          v-if="partialEmbedding"
                          lg="auto"
                          md="12"
                        >
                          <div class="form-label">
                            <label>{{ $t('grossing.content.specialPreserved') }}</label>
                          </div>
                          <div class="d-flex align-items-center">
                            <b-form-group
                              size="sm"
                              class="mb-0"
                            >
                              <b-form-radio-group
                                v-model="selectedSpecialPreserveOption"
                                :disabled="isReadOnly"
                                name="radio-btn-outline"
                                class="btn__group"
                                size="sm"
                                buttons
                                button-variant="outline-primary"
                                :options="specialPreserveOptions"
                              />
                            </b-form-group>
                          </div>
                        </b-col>
                        <b-col
                          v-if="partialEmbedding"
                          xl="3"
                          lg="3"
                          md="12"
                        >
                          <div class="form-label">
                            <label>{{ $t('grossing.content.preservedDays') }}</label>
                          </div>
                          <vt-input
                            v-model="daysToPreserve"
                            qa="section-sample-days-to-preserve-input"
                            :disabled="isReadOnly || selectedSpecialPreserveOption !== PRESERVE_OPTION_DAYS"
                            class="form-control-sm"
                            type="number"
                            :min="selectedSampleTypeLaboratoryDetails.defaultDaysBeforeDisposal"
                          />
                        </b-col>
                      </b-row>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col
                      v-if="hasURVActive"
                      xl="6"
                    >
                      <b-row class="pt-2 mt-2 h-auto">
                        <div class="d-flex align-items-center col-auto">
                          <span class="ap-title ap-title__divider">{{ $t('diagnosis.labels.valueUnitSample') }}</span>
                        </div>
                        <div class="col">
                          <hr>
                        </div>
                      </b-row>
                      <b-row>
                        <b-col cols="3">
                          <div class="form-label">
                            <label>{{ $t('diagnosis.labels.value') }}</label>
                          </div>
                          <vt-input
                            qa="section-sample-urv-input"
                            disabled
                            class="form-control-sm"
                            :value="selectedURV ? selectedURV.totalValue : ''"
                            type="text"
                          />
                        </b-col>
                        <b-col cols="9">
                          <div class="form-label">
                            <label>{{ $t('diagnosis.labels.description') }}</label>
                          </div>
                          <vt-combo-select
                            id="sectionSampleVTModelSelectURV"
                            v-model="selectedURV"
                            qa="section-sample-urv-description-div"
                            :disabled="isReadOnly"
                            class="nowrap max-size"
                            track-by="id"
                            label="name"
                            :option-loader="urvValuableModelConfigurations"
                            @input="customLabelURV($event)"
                          />
                        </b-col>
                      </b-row>
                    </b-col>
                    <b-col
                      v-if="hasUCLActive"
                      xl="6"
                    >
                      <b-row class="pt-2 mt-2 h-auto">
                        <div class="d-flex align-items-center col-auto">
                          <span class="ap-title ap-title__divider">{{ $t('diagnosis.labels.workUnitSample') }}</span>
                        </div>
                        <div class="col">
                          <hr>
                        </div>
                      </b-row>
                      <b-row>
                        <b-col cols="3">
                          <div class="form-label">
                            <label>{{ $t('diagnosis.labels.value') }}</label>
                          </div>
                          <vt-input
                            qa="section-sample-ucl-input"
                            disabled
                            class="form-control-sm"
                            :value="selectedUCL ? selectedUCL.totalValue : ''"
                            type="text"
                          />
                        </b-col>
                        <b-col cols="9">
                          <div class="form-label">
                            <label>{{ $t('diagnosis.labels.description') }}</label>
                          </div>
                          <vt-combo-select
                            id="sectionSampleVTModelSelectUCL"
                            v-model="selectedUCL"
                            qa="section-sample-ucl-description-div"
                            :disabled="isReadOnly"
                            class="nowrap max-size"
                            track-by="id"
                            label="name"
                            :option-loader="uclValuableModelConfigurations"
                            @input="customLabelUCL($event)"
                          />
                        </b-col>
                      </b-row>
                    </b-col>
                  </b-row>
                </div>
              </b-col>
            </b-row>

            <!--Additional block-->
            <b-row class="mt-3">
              <b-col>
                <div class="form-card">
                  <b-row class="mb-2">
                    <div class="d-flex align-items-center col-auto">
                      <span class="ap-title ap-title__section">
                        <i
                          class="far fa-layer-plus mr-2"
                          aria-hidden="true"
                        />
                        {{ $t('global.supplementaryData') }}
                      </span>
                    </div>
                  </b-row>
                  <b-row
                    v-if="isGrossingWorkflow"
                    class="col-grid"
                  >
                    <b-col
                      class="field"
                      xl="3"
                      lg="6"
                    >
                      <div class="form-label">
                        <label>{{ $t('diagnosis.labels.grossingResponsible') }}
                          <i
                            v-if="sampleInformation.automaticGrossingResponsible"
                            v-tooltip="$tc('global.automaticAssignment')"
                            class="fal fa-magic ml-1"
                            aria-hidden="true"
                          />
                        </label>
                      </div>
                      <vt-combo-select
                        v-model="sampleInformation.grossingResponsible"
                        qa="section-sample-grossing-responsible-div"
                        :disabled="$_disabledGrossingResponsible(sampleInformation)"
                        class="nowrap"
                        label="fullName"
                        :option-loader="getPersonnelGrossingResponsible"
                        :open-direction="'top'"
                      />
                    </b-col>
                  </b-row>
                  <b-row
                    v-if="isCytologyWorkflow"
                    class="col-grid mt-3"
                  >
                    <b-col
                      class="field"
                      xl="3"
                      lg="6"
                    >
                      <div class="form-label">
                        <label>{{ $t('diagnosis.labels.processingResponsible') }}
                          <i
                            v-if="sampleInformation.automaticGrossingResponsible"
                            v-tooltip="$tc('global.automaticAssignment')"
                            class="fal fa-magic ml-1"
                            aria-hidden="true"
                          /></label>
                      </div>
                      <vt-combo-select
                        v-model="sampleInformation.grossingResponsible"
                        qa="section-sample-grossing-responsible-div"
                        :disabled="$_disabledGrossingResponsible(sampleInformation)"
                        class="nowrap"
                        label="fullName"
                        :option-loader="getPersonnelProcessingResponsible"
                        :open-direction="'top'"
                      />
                    </b-col>
                    <b-col
                      class="field"
                      xl="3"
                      lg="6"
                    >
                      <div class="form-label">
                        <label>{{ $t('diagnosis.grid.sampleGrid.specialist') }}
                          <i
                            v-if="sampleInformation.automaticSpecialist"
                            v-tooltip="$tc('global.automaticAssignment')"
                            class="fal fa-magic ml-1"
                            aria-hidden="true"
                          />
                        </label>
                      </div>
                      <vt-combo-select
                        v-model="sampleInformation.specialist"
                        qa="section-sample-cytology-expert-div"
                        :disabled="$_disabledSpecialist(sampleInformation)"
                        class="nowrap"
                        label="fullName"
                        :option-loader="getPersonnelCytologyExpert"
                        :open-direction="'top'"
                      />
                    </b-col>
                  </b-row>
                </div>
              </b-col>
            </b-row>
          </b-container>
        </div>
      </div>
      <div class="d-flex justify-content-end">
        <vt-loading-button
          v-if="$_canEditSample()"
          class="btn__outline btn-sm mt-2"
          qa="section-sample-save-button"
          :params="{ promise: save, shortkeys: ['alt', 's'] }"
        >
          <i
            class="fal fa-save mr-2"
            aria-hidden="true"
          />
          {{ $t('global.save') }}
        </vt-loading-button>
      </div>
    </div>
    <samples-update-util ref="samplesUpdateUtil" />
  </div>
</template>

<script>
import { commonSampleClassificationService } from '@/api/resources/common/sample-classification/sample-classification.service'
import SamplesUpdateUtil from '@/components/common/SamplesUpdateUtil'
import sampleTagService from '@/api/resources/sampleTagService'
import { StoreUtils } from '@/shared/util/store-utils'
import valuableConceptResource from '@/api/resources/ValuableConceptResource'
import diagnosisSampleService from '@/modules/diagnosisModule/services/DiagnosisSampleService'
import { personnelService } from '@/api/resources/personnelService'
import sampleTypeLaboratoryService from '@/api/resources/sampleTypeLaboratoryService'
import { cloneDeep, isEqual } from 'lodash'
import VtBubble from '@/components/common/VtBubble'
import { SAMPLE_TYPE_WORKFLOW } from '@/api/resources/common/commonSampleService'
import { SampleOperationGuardMixin } from '@/api/operations/mixins/guards/sample-operation-guard.mixin'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import AlertBanner from '@/components/common/AlertBanner/alert-banner.vue'
import { CommonResponsibilityCodeEnum } from '@/api/services/common/responsibility/common-responsibility.dto'

const PRESERVE_OPTION_NO = 'no'
const PRESERVE_OPTION_INDEFINITE = 'indefinite'
const PRESERVE_OPTION_DAYS = 'days'
export default {
    name: 'SectionSample',
    components: {
        SamplesUpdateUtil,
        VtBubble,
        AlertBanner
    },
    mixins: [
        SampleOperationGuardMixin,
        CaseOperationGuardMixin,
        IncidentOperationGuardMixin
    ],
    props: {
        labId: { type: Number, required: true },
        caseStudy: { type: Object, required: true },
        sample: { type: Object, required: true }
    },
    data() {
        return {
            SAMPLE_TYPE_WORKFLOW: SAMPLE_TYPE_WORKFLOW,
            sampleInformation: {
                daysBeforeDisposal: null,
                grossingResponsible: null,
                automaticGrossingResponsible: false,
                automaticSpecialist: false,
                mostLikelyMorphologicCode: null,
                originalConcurrencyUpdatedAt: null,
                procedure: null,
                sampleTagIds: [],
                sampleType: null,
                specialPreserve: null,
                specialist: null,
                topographicCode: null,
                uclValuableConceptId: null,
                urvValuableConceptId: null,
                metadata: {
                    operationGuards: {},
                    editionDisabledFields: {}
                }
            },
            sampleInformationTags: [],
            originalTags: [],
            partialEmbedding: null,
            originalSample: null,
            sampleLoaded: false,
            sampleLabel: null,
            selectedURV: null,
            selectedUCL: null,
            selectedSpecialPreserveOption: null,
            daysToPreserve: null,
            sampleValuableModelConfigs: [],
            sampleTags: [],
            selectedSampleTypeLaboratoryDetails: null,
            specialPreserveOptions: [
                { text: this.$t('grossing.content.presOptionNo'), value: PRESERVE_OPTION_NO },
                { text: this.$t('grossing.content.presOptionIndefinite'), value: PRESERVE_OPTION_INDEFINITE },
                { text: this.$t('grossing.content.presOptionDays'), value: PRESERVE_OPTION_DAYS }
            ],
            totalURV: '000',
            totalUCL: '000'
        }
    },
    computed: {
        casId() { return this.caseStudy.casId },
        caseTypeId() { return this.caseStudy.caseType.id },
        samId() { return this.sample.samId },

        disabledPopoverButton() {
            return !this.$_canCreateIncident() && !this.$_canAttachDocumentToSample()
        },
        PRESERVE_OPTION_DAYS() {
            return PRESERVE_OPTION_DAYS
        },
        hasURVActive() {
            const activeModule =
                StoreUtils.selectedLaboratoryConfiguration?.activeModules.find(
                    module => module.moduleCode === 'configuration.parameter.laboratory.module.accountancy.urv.active')
            return !!activeModule?.activeStatus
        },
        hasUCLActive() {
            const activeModule =
                StoreUtils.selectedLaboratoryConfiguration?.activeModules.find(
                    module => module.moduleCode === 'configuration.parameter.laboratory.module.accountancy.ucl.active')
            return !!activeModule?.activeStatus
        },
        isReadOnly() {
            const study = this.sample.caseStudy ?? this.caseStudy
            return !this.$_isCaseEditable(study.status.code) || !this.$_canEditSample()
        },
        isGrossingWorkflow() {
            return this.sampleInformation?.sampleType.workflow === SAMPLE_TYPE_WORKFLOW.GROSSING_BIOPSIES.code
        },
        isCytologyWorkflow() {
            return this.sampleInformation?.sampleType.workflow === SAMPLE_TYPE_WORKFLOW.PROCESSING_CYTOLOGIES.code
        },
        disabledTagsEdition() {
            return !this.$_canEditSample()
        }
    },
    watch: {
        selectedSpecialPreserveOption(value) {
            if (this.partialEmbedding) {
                switch (value) {
                case PRESERVE_OPTION_NO:
                    this.sampleInformation.specialPreserve = false
                    this.daysToPreserve = this.selectedSampleTypeLaboratoryDetails.defaultDaysBeforeDisposal
                    this.sampleInformation.daysBeforeDisposal = this.selectedSampleTypeLaboratoryDetails.defaultDaysBeforeDisposal
                    break
                case PRESERVE_OPTION_INDEFINITE:
                    this.sampleInformation.specialPreserve = true
                    this.daysToPreserve = null
                    this.sampleInformation.daysBeforeDisposal = null
                    break
                case PRESERVE_OPTION_DAYS:
                    this.sampleInformation.specialPreserve = true
                    this.daysToPreserve = this.sampleInformation.daysBeforeDisposal || this.selectedSampleTypeLaboratoryDetails.defaultDaysBeforeDisposal
                    this.sampleInformation.daysBeforeDisposal = this.daysToPreserve
                    break
                }
            } else {
                this.daysToPreserve = null
                this.sampleInformation.daysBeforeDisposal = null
            }
        },
        daysToPreserve(value) {
            if (this.partialEmbedding) {
                switch (this.selectedSpecialPreserveOption) {
                case PRESERVE_OPTION_NO:
                    this.sampleInformation.daysBeforeDisposal = this.originalSample.daysBeforeDisposal
                    break
                case PRESERVE_OPTION_INDEFINITE:
                    this.sampleInformation.daysBeforeDisposal = null
                    break
                case PRESERVE_OPTION_DAYS:
                    this.sampleInformation.daysBeforeDisposal = value
                    break
                }
            } else {
                this.sampleInformation.daysBeforeDisposal = null
            }
        }
    },
    async created() {
        await Promise.all([
            this.getSample(),
            this.loadSampleTags()
        ])
        await this.loadSampleTypeLaboratoryDetails()
    },
    methods: {
        async reloadTab() {
            await Promise.all([
                this.getSample(),
                this.loadSampleTags()
            ])
            await this.loadSampleTypeLaboratoryDetails()
        },
        showInGlobalLocator(label) {
            this.$eventHub.$emit('show-in-global-locator', label)
        },
        hasBeenChanged() {
            const sampleInformation = this.removeExternalOptionFromEntityObjects(this.sampleInformation)
            const sampleHasBeenChanged = !!this.originalSample && (!isEqual(this.originalSample, sampleInformation))
            const tagsHasBeenChanged = !isEqual(this.originalTags, this.sampleInformationTags)
            return sampleHasBeenChanged || tagsHasBeenChanged
        },
        async save() {
            if (!this.hasBeenChanged()) {
                return
            }
            // TODO: Add validation of sample before updating it
            let updatedSample = false
            this.updateLoading(true)
            this.sampleInformation.sampleTagIds = this.sampleInformationTags.map((tag) => tag.id)
            if (this.$_isCaseValidated(this.caseStudy?.status.code)) {
                const [err] = await this.toCall(sampleTagService.updateTagsOfSample(this.labId, this.casId, this.samId,
                    this.sampleInformation.sampleTagIds))
                if (err) {
                    this.updateLoading(false)
                    this.showErrorModalGlobal()
                    return
                }
                updatedSample = true
            } else {
                this.sampleInformation.sampleTypeId = this.sampleInformation.sampleType.id
                this.sampleInformation.topographicCodeId = this.sampleInformation.topographicCode.id
                this.sampleInformation.procedureId = this.sampleInformation.procedure ? this.sampleInformation.procedure.id : null
                this.sampleInformation.mostLikelyMorphologicCodeId = this.sampleInformation.mostLikelyMorphologicCode ? this.sampleInformation.mostLikelyMorphologicCode.id : null
                this.sampleInformation.grossingResponsibleId = this.sampleInformation.grossingResponsible ? this.sampleInformation.grossingResponsible.id : null
                this.sampleInformation.specialistId = this.sampleInformation.specialist ? this.sampleInformation.specialist.id : null
                const response = await this.$refs.samplesUpdateUtil.updateSample(this.labId, this.casId, this.samId, this.sampleInformation, diagnosisSampleService)
                if (Array.isArray(response)) {
                    updatedSample = !response[0]
                } else {
                    updatedSample = !!response
                }
            }
            this.updateLoading(false)
            if (updatedSample) {
                if (
                    this.originalSample.topographicCode.id !== this.sampleInformation.topographicCode.id ||
                    this.originalSample.sampleType.id !== this.sampleInformation.sampleType.id
                ) {
                    this.$emit('updateSamplesCards')
                }
                if (
                    this.originalSample.urvValuableConceptId !== this.sampleInformation.urvValuableConceptId ||
                    this.originalSample.uclValuableConceptId !== this.sampleInformation.uclValuableConceptId
                ) {
                    this.$emit('updateCase')
                }
                this.showToastSuccessGlobal()
                this.$emit('updateSamplesCards')
            }
            await this.getSample()
            return updatedSample
        },
        async loadSampleTags() {
            const [error, response] = await this.toCall(sampleTagService.findAllActiveForLaboratory(this.labId))
            if (error) {
                this.showErrorModalGlobal(this.$t('grossing.error.loadingSampleTags'))
            } else {
                this.sampleTags = response
            }
        },
        async loadSampleTypeLaboratoryDetails() {
            const [error, response] = await this.toCall(sampleTypeLaboratoryService.findSampleTypeLaboratoryBySampleTypeId(
                this.labId, this.sampleInformation.sampleType.id))
            if (error) {
                this.showErrorModalGlobal(this.$t('grossing.error.loadingSampleTypeLaboratoryDetails'))
            } else {
                this.selectedSampleTypeLaboratoryDetails = response
            }
        },
        loadTopographicCodes() {
            return commonSampleClassificationService.getCommonTopographicCodesSampleClassificationsByLaboratory(
                { laboratoryId: this.sample.currentLaboratory.labId }
            )
        },
        loadSampleTypes() {
            return commonSampleClassificationService.getCommonSampleTypeSampleClassificationsByLaboratory(
                {
                    laboratoryId: this.sample.currentLaboratory.labId,
                    caseTypeIds: [this.caseTypeId]
                }
            )
        },
        loadMostLikelyMorphologicCodes() {
            return commonSampleClassificationService.getCommonMostLikelyMorphologicCodesSampleClassificationsByLaboratory(
                { laboratoryId: this.sample.currentLaboratory.labId }
            )
        },
        loadProcedures() {
            return commonSampleClassificationService.getCommonProceduresSampleClassificationsByLaboratory(
                { laboratoryId: this.sample.currentLaboratory.labId }
            )
        },
        getPersonnelGrossingResponsible() {
            return personnelService.getPersonnelByLaboratoryAndResponsibility(this.sample.currentLaboratory.labId, CommonResponsibilityCodeEnum.GROSSING_RESPONSIBLE)
        },
        getPersonnelProcessingResponsible() {
            return personnelService.getPersonnelByLaboratoryAndResponsibility(this.sample.currentLaboratory.labId, CommonResponsibilityCodeEnum.PROCESSING_RESPONSIBLE)
        },
        getPersonnelCytologyExpert() {
            return personnelService.getPersonnelByLaboratoryAndResponsibility(this.sample.currentLaboratory.labId, CommonResponsibilityCodeEnum.CYTOLOGY_EXPERT)
        },
        urvValuableModelConfigurations() {
            return valuableConceptResource.allValuableConcepts(this.labId, 'URV')
        },
        uclValuableModelConfigurations() {
            return valuableConceptResource.allValuableConcepts(this.labId, 'UCL')
        },
        customLabelURV(data) {
            this.selectedURV = data
            this.sampleInformation.urvValuableConceptId = data.id
        },
        customLabelUCL(data) {
            this.selectedUCL = data
            this.sampleInformation.uclValuableConceptId = data.id
        },
        async findSampleValuableModelConfigs() {
            const valueURV = await valuableConceptResource.findValuableConceptsBySample(this.labId, this.casId, this.samId, 'URV')
            const valueUCL = await valuableConceptResource.findValuableConceptsBySample(this.labId, this.casId, this.samId, 'UCL')
            this.selectedURV = valueURV[0]
            this.selectedUCL = valueUCL[0]
            this.sampleInformation.urvValuableConceptId = valueURV[0] ? valueURV[0].id : null
            this.sampleInformation.uclValuableConceptId = valueUCL[0] ? valueUCL[0].id : null
        },
        async getSample() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(diagnosisSampleService.getSampleById(this.labId, this.casId, this.samId))
            this.updateLoading(false)

            if (err) {
                this.showErrorModalGlobal()
                return
            }

            await this.mapSampleDataToShow(response)
            this.sampleLoaded = true
        },
        async mapSampleDataToShow(sample) {
            this.sampleInformation.daysBeforeDisposal = sample.daysBeforeDisposal
            this.sampleInformation.grossingResponsible = sample.grossingResponsible
            this.sampleInformation.automaticGrossingResponsible = sample.automaticGrossingResponsible
            this.sampleInformation.automaticSpecialist = sample.automaticSpecialist
            this.sampleInformation.mostLikelyMorphologicCode = sample.mostLikelyMorphologicCode
            this.sampleInformation.originalConcurrencyUpdatedAt = sample.originalConcurrencyUpdatedAt
            this.sampleInformation.procedure = sample.procedure
            this.sampleInformation.sampleType = sample.sampleType
            this.sampleInformation.specialist = sample.specialist
            this.sampleInformation.specialPreserve = sample.specialPreserve
            this.sampleInformation.topographicCode = sample.topographicCode
            this.sampleInformation.status = sample.status
            this.sampleInformation.metadata = sample.metadata
            this.sampleLabel = sample.label
            this.partialEmbedding = sample.partialEmbedding
            this.sampleInformationTags = sample.tags
            this.totalUCL = sample.uclTotalSum
            this.totalURV = sample.urvTotalSum
            await this.findSampleValuableModelConfigs()
            await this.applyPostSampleLoadingChanges()
        },
        async applyPostSampleLoadingChanges() {
            await this.loadSampleTypeLaboratoryDetails()
            this.originalSample = cloneDeep(this.sampleInformation)
            this.originalTags = cloneDeep(this.sampleInformationTags)
            if (this.sampleInformation.specialPreserve) {
                this.daysToPreserve = this.sampleInformation.daysBeforeDisposal
                if (this.sampleInformation.daysBeforeDisposal) {
                    this.selectedSpecialPreserveOption = PRESERVE_OPTION_DAYS
                } else {
                    this.selectedSpecialPreserveOption = PRESERVE_OPTION_INDEFINITE
                }
            } else {
                this.selectedSpecialPreserveOption = PRESERVE_OPTION_NO
                this.daysToPreserve = this.sampleInformation.daysBeforeDisposal | this.selectedSampleTypeLaboratoryDetails.defaultDaysBeforeDisposal
            }
        },
        async changeSelectedSampleType(newSampleType) {
            /* TODO: Esperar a solucion para limpieza de responsables */
            /* if (this.sampleInformation.sampleType.workflow !== newSampleType.workflow) {
                this.sampleInformation.grossingResponsibleId = null
            } */
            this.sampleInformation.sampleType = newSampleType
            if (newSampleType) {
                await this.loadSampleTypeLaboratoryDetails()
                if (this.partialEmbedding && this.selectedSpecialPreserveOption === PRESERVE_OPTION_NO) {
                    this.daysToPreserve = this.selectedSampleTypeLaboratoryDetails.defaultDaysBeforeDisposal
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>

    .sample-scroll-section{
        height: calc(100vh - 330px);
        padding-right: 5px;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .radio-btn .btn {
        font-size: font-size(small) !important;
    }
    .switch {
        position: relative;
        display: inline-block;
        width: 40px;
        height: 20px;
        background-color: rgba(0, 0, 0, 0.25);
        border-radius: 20px;
        transition: all 0.3s;
        cursor: pointer;
    }

    .switch::after {
        content: "";
        position: absolute;
        width: 18px;
        height: 18px;
        border-radius: 18px;
        background-color: white;
        top: 1px;
        left: 1px;
        transition: all 0.3s;
    }

    input[type="checkbox"]:checked + .switch::after {
        transform: translateX(20px);
    }

    input[type="checkbox"]:checked + .switch {
        background-color: vt-color(primary);
    }

    .offscreen {
        position: absolute;
        left: -9999px;
    }
    .view-more {
        color: vt-color(primary);
        transition: 0.2s;
        cursor: pointer;
    }

    .view-more:hover {
        color: vt-color(dark-primary);
    }

    .pop-labels {
        width: 200px;
    }

    .pop-labels-row {
        border-bottom: 1px solid rgba(0, 0, 0, 0.2);
    }

    .section-sample .value {
        color: vt-color(primary);
    }

    .section-sample .main {
        font-size: font-size(normal);
        margin-bottom: 0;
    }

    .section-sample .form-label {
        font-size: font-size(small);
    }

    .section-sample .col-grid .field {
        margin-bottom: 15px;
    }

    .section-sample .section-sample-layout-height {
        min-height: 0;
    }

    .tag-indicator {
        position: absolute;
        right: -5px;
        top: 12px;
    }

    /*--Styles for large screen--*/
    @media (max-width: 1300px) {
        .sample-scroll-section{
            height: calc(100vh - 327px);
        }

        .section-sample .max-size {
            max-width: 280px !important;
        }
    }

    /*--Sizes for laptop screen--*/
    @media only screen and (max-width: 1366px) and (min-width: 1360px) {
        .sample-scroll-section{
            height: calc(100vh - 335px);
        }
    }

</style>
